import { Info } from '@mui/icons-material';
import { Divider, Grid, Tooltip, Typography, styled } from '@mui/material';
import { saveAs } from 'file-saver';
import { isEmpty } from 'lodash-es';
import { useMemo, useState } from 'react';
import {
  downloadJobDescriptionNew,
  IJob,
  IJobEnhancePayload,
  IJobRevenue,
  IUserDTO,
} from 'wisestep-api';
import {
  ScrollableTabs,
  TabTextVariant,
  WsColors,
  WsColorsNew,
  WsDrawer,
  WsMoreOptionButton,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useUserContext } from '../../../context';
import CardWrapper from '../../../features/automatedEmailCampaignSettings/childRoutes/CardWrapper';
import { EmptyResponseCard } from '../../../features/candidateLayout/components/childComponets';
import { copyStringToClipboard } from '../../../features/candidateLayout/components/childComponets/CandidateDetailsCard/Helper';
import { ChipStyle } from '../../../features/candidateLayout/components/childComponets/expandableText/ExpandableChips';
import Empty_Recruiter_Questions from '../../../images/Empty_Recruiter_Questions.svg';
import Empty_skills from '../../../images/Empty_Skills.svg';
import Empty_Job_description_image from '../../../images/Empty_Submission_Notes.svg';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { calculateProgress } from '../../jobs/helper';
import { useJobEnhancement } from '../../jobs/hooks/useJobEnhancement';
import AIEnhanceBar from '../../jobs/post/components/AIEnhanceBar';
import { JobDetailsTabList } from '../components/helper/JobsCrmHelper';
import InfoDetailsCard from '../components/infoDetailsCard/InfoDetailsCard';
import { JobBoardPublishCard } from '../components/jobBoardPublishCard';
import { TemplatesCard } from '../components/templateCard';
import useJobEvalutionTemplates from '../hooks/useJobEvalutionTemplates';
import useJobInfoTemplates from '../hooks/useJobInfoTemplates';
import useJobRevenue from '../hooks/useJobRevenue';
import JobDetailsAIEnhancement from '../jobDetailsEnhancement/JobDetailsAIEnhancement';
import { getJobBasicDetails, getRevenueInformation } from '../utils/jobDetailsHelper';

interface InfoIconProps {
  progress: number;
}

const InfoIcon = styled(Info, {
  shouldForwardProp: (prop) => prop !== 'progress',
})<InfoIconProps>(({ theme, progress }) => ({
  color: progress < 90 ? WsColorsNew.danger_400 : WsColorsNew.success_400,
  fontSize: 18,
  cursor: 'pointer',
  '&:hover': {
    color: progress < 90 ? theme.palette.error.dark : theme.palette.success.dark,
  },
}));

const JobDetails = ({ job }: { job: IJob }) => {
  const { jobRevenue } = useJobRevenue(job?.id ?? '');
  const setNotification = useNotificationStore((state) => state.setNotification);

  const jobBasicDetails = useMemo(() => getJobBasicDetails(job), [job]);
  const jobRevenueInfoDetails = useMemo(
    () => getRevenueInformation(jobRevenue ?? ({} as IJobRevenue), job),
    [jobRevenue, job]
  );
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const isAssigned =
    loggedInUser?.email &&
    job?.assignedTo?.filter((user) => user.recruiterId == loggedInUser.email);

  const accessPermisson =
    job?.canManageJob || job?.createdBy === loggedInUser?.email || isEmpty(isAssigned);

  const downloadJD = async () => {
    try {
      if (job.id) {
        const downloadDetails = await downloadJobDescriptionNew(job.id);
        const headers = downloadDetails.headers;
        const file = new Blob([downloadDetails.data], {
          type: headers['content-type'],
        });
        saveAs(file, headers['content-disposition'].replace('attachment; filename=', ''));
      }
    } catch (error) {
      console.error('Failed to download job description:', error);
      setNotification(warning('Failed to download job description !!'));
    }
  };

  const copyJD = (JD: string) => {
    copyStringToClipboard(JD);
    setNotification(success('Copied Successfully !!'));
  };

  const onMoreOptionClick = (option: string) => {
    const handler = moreOptionHandlers.get(option);
    if (!handler) {
      console.warn(`No handler found for option: ${option}`);
      return;
    }
    handler();
  };

  const moreOptionHandlers = new Map<string, () => void>([
    ['Download JD', () => downloadJD()],
    ['Copy JD', () => copyJD(job?.description ?? '')],
  ]);

  const [tabIndex, setTabIndex] = useState<number>(0);
  const selectedTabIndex = (index: number) => setTabIndex(index);
  const { evalutionTemplates } = useJobEvalutionTemplates(job?.id ?? '');
  const { infoTemplates } = useJobInfoTemplates(job?.id ?? '');
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const { isAIEnhanced, enhanceJob, enhancedData, isLoading } = useJobEnhancement();
  const onEnhanceClick = async () => {
    setOpenDrawer(true);
    try {
      const payload: IJobEnhancePayload = {
        title: job?.title || '',
        locations: job?.locations || [],
        description: job?.description || '',
        skills: job?.skills || [],
        jobType: job?.jobType || null,
      };

      await enhanceJob(payload);
    } catch (error) {
      console.error('Enhancement failed:', error);
    }
  };

  const onDrawerClose = () => setOpenDrawer(false);

  const progress = useMemo(
    () => calculateProgress(job?.description ?? '', job?.skills ?? []) || 0,
    [job?.description, job?.skills]
  );

  const QualityTooltip = () => (
    <Tooltip
      title={
        progress < 90 ? (
          <>
            <WsTypography variant={WsTypoVariants.BODY2}>
              {`Job Quality : ${progress}%`}
            </WsTypography>
            <WsTypography variant={WsTypoVariants.BODY2}>
              Optimise Job using AI Enhance
            </WsTypography>
          </>
        ) : (
          <WsTypography variant={WsTypoVariants.BODY2}>{`Job Quality : ${progress}%`}</WsTypography>
        )
      }
      arrow
    >
      <InfoIcon progress={progress} />
    </Tooltip>
  );

  return (
    <Grid display="flex" columnGap={1.5} mt={1}>
      <Grid item xs={5} display="flex" direction="column" rowGap={1}>
        <InfoDetailsCard details={jobBasicDetails} title="Basic Information" />
        <InfoDetailsCard details={jobRevenueInfoDetails} title="Revenue Information" />
        <JobBoardPublishCard job={job} />
      </Grid>
      <Grid xs={7} display="flex" direction="column" rowGap={1}>
        {progress < 90 && accessPermisson && (
          <AIEnhanceBar
            progress={progress}
            title="Job Quality"
            onEnhanceClick={onEnhanceClick}
            disabled={isAIEnhanced}
            withBackground={false}
          />
        )}
        <CardWrapper>
          <Grid display="flex" justifyContent="space-between" alignItems="center">
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <WsTypography bold variant={WsTypoVariants.SUB_TITLE}>
                  Job Details
                </WsTypography>
              </Grid>
              <Grid item>
                <QualityTooltip />
              </Grid>
            </Grid>
            <Grid item display="flex" alignItems="center" columnGap={1}>
              <WsMoreOptionButton
                onItemClick={(item) => onMoreOptionClick(item)}
                items={['Download JD', 'Copy JD']}
              />
            </Grid>
          </Grid>
          <Grid>
            <ScrollableTabs
              scrollList={JobDetailsTabList}
              getTabIndex={selectedTabIndex}
              variant={TabTextVariant.SUB_TITLE}
              defaultValue={tabIndex}
            />
            <Grid minHeight={300}>
              {tabIndex === 0 &&
                (job?.description ? (
                  <Typography
                    fontWeight={500}
                    dangerouslySetInnerHTML={{
                      __html: job?.description ?? '',
                    }}
                  />
                ) : (
                  <EmptyResponseCard
                    src={Empty_Job_description_image}
                    subText="No job description has been provided."
                    text=""
                    sx={{ minHeight: 300 }}
                  />
                ))}
              {tabIndex === 1 && (
                <Grid display="flex" direction="column" rowGap={1} py={1}>
                  {job?.questions?.length ? (
                    (job?.questions ?? []).map((question, index) => (
                      <WsTypography key={index}>{`${index + 1}. ${question}`}</WsTypography>
                    ))
                  ) : (
                    <EmptyResponseCard
                      src={Empty_Recruiter_Questions}
                      subText="No questions have been added for this job."
                      text=""
                      sx={{ minHeight: 300 }}
                    />
                  )}
                </Grid>
              )}
              {tabIndex === 2 && (
                <TemplatesCard
                  evalutionTemplates={evalutionTemplates?.map((e) => e?.templateName) ?? []}
                  infoTemplates={infoTemplates?.map((e) => e?.categoryName) ?? []}
                  jobSubmissionTemplate={job?.submissionFormTemplete ?? ''}
                />
              )}
            </Grid>
          </Grid>
        </CardWrapper>
        <CardWrapper>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
                Skills
              </WsTypography>
            </Grid>
            <Grid item>
              <QualityTooltip />
            </Grid>
          </Grid>
          <Divider sx={{ borderColor: WsColors.NEUTRAL_100 }} />
          <Grid container gap={1} py={2} flexWrap="wrap">
            {job?.skills?.length ? (
              (job?.skills ?? []).map((item, index) => <ChipStyle key={index} label={item} />)
            ) : (
              <EmptyResponseCard
                src={Empty_skills}
                subText="No skills."
                text=""
                sx={{ minHeight: 300 }}
              />
            )}
          </Grid>
        </CardWrapper>
      </Grid>
      {openDrawer && (
        <WsDrawer open={openDrawer} onClose={onDrawerClose} title="AI Enhance">
          <JobDetailsAIEnhancement
            job={job}
            enhancedData={enhancedData}
            isLoading={isLoading}
            isAIEnhanced={isAIEnhanced}
            closeDrawer={onDrawerClose}
          />
        </WsDrawer>
      )}
    </Grid>
  );
};

export default JobDetails;
